import { useEffect, useRef, useCallback, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { fetchAiCredits } from "@store/globalSlice";
import { RootState } from "@store/store";
import type { UserAssetV2Record } from "@apis/http/api";
import {
  GENERATED_VIDEO_ASSETS_REFRESH_EVENT,
  fetchGeneratedVideoAssetsPage,
  isProcessingAsset,
} from "@pages/library/hooks/useGeneratedVideoAssets";

/**
 * 轮询生成列表：每 30 秒刷新，90 分钟后自动停止；
 * 进入 library/generated 页面或 hover Library 时通过 resetPolling 重置计时；
 * 在「已生成」页且有进行中任务时，从后台/其他 Tab 切回会重新开始轮询并立即拉取一次。
 */
export const useLibraryPolling = () => {
  const dispatch = useAppDispatch();
  const [items, setItems] = useState<UserAssetV2Record[]>([]);
  const processingTasks = useMemo(() => items.filter(isProcessingAsset), [items]);
  const { focusType, videoType } = useAppSelector((state: RootState) => state.libraryReducer);
  const { pathname } = useLocation();

  const POLL_INTERVAL = 30 * 1000;
  const POLL_MAX_DURATION = 90 * 60 * 1000;

  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const deadlineRef = useRef(0);
  const skipNextFetchRef = useRef(false);
  const requestSeqRef = useRef(0);
  const prevProcessingIdsRef = useRef<Set<string>>(new Set());
  const hasProcessingTasks = processingTasks.length > 0;
  const isGeneratedPage = pathname === "/library" && focusType === "video" && videoType === "generated";

  const stopPolling = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = undefined;
    deadlineRef.current = 0;
  }, []);

  const refresh = useCallback(async () => {
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    try {
      const result = await fetchGeneratedVideoAssetsPage({ pageNo: 1, pageSize: 100 });
      if (requestSeqRef.current !== requestSeq) return;
      setItems(result.data ?? []);
    } catch {
      if (requestSeqRef.current !== requestSeq) return;
      setItems([]);
    }
  }, []);

  const startPolling = useCallback(() => {
    deadlineRef.current = Date.now() + POLL_MAX_DURATION;
    if (intervalRef.current) return;
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
    } else {
      refresh();
    }
    intervalRef.current = setInterval(() => {
      if (Date.now() > deadlineRef.current) {
        stopPolling();
        return;
      }
      refresh();
    }, POLL_INTERVAL);
  }, [refresh, stopPolling]);

  /** 清掉定时器后重建轮询：重置 30 分钟截止并立即拉一次（用于切回 Tab） */
  const restartPolling = useCallback(() => {
    stopPolling();
    skipNextFetchRef.current = false;
    startPolling();
  }, [stopPolling, startPolling]);

  useEffect(() => {
    refresh();
    skipNextFetchRef.current = true;
  }, [refresh]);

  useEffect(() => {
    const handleRefresh = () => {
      void refresh();
    };
    window.addEventListener(GENERATED_VIDEO_ASSETS_REFRESH_EVENT, handleRefresh);
    return () => window.removeEventListener(GENERATED_VIDEO_ASSETS_REFRESH_EVENT, handleRefresh);
  }, [refresh]);

  useEffect(() => {
    if (hasProcessingTasks) {
      startPolling();
    } else {
      stopPolling();
    }
    return stopPolling;
  }, [hasProcessingTasks, startPolling, stopPolling]);

  useEffect(() => {
    if (isGeneratedPage && hasProcessingTasks) {
      startPolling();
    }
  }, [isGeneratedPage, hasProcessingTasks, startPolling]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!isGeneratedPage || !hasProcessingTasks) return;
      restartPolling();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [isGeneratedPage, hasProcessingTasks, restartPolling]);

  // 检测「进行中/排队中 → 失败」，刷新积分余额（失败时积分会退还）
  useEffect(() => {
    const currentData = items ?? [];
    const prevIds = prevProcessingIdsRef.current;

    if (prevIds.size > 0) {
      const hasNewFailure = currentData.some((item: any) => prevIds.has(item.id) && item.jobItem?.status === "failed");
      if (hasNewFailure) {
        dispatch(fetchAiCredits());
      }
    }

    prevProcessingIdsRef.current = new Set(
      currentData
        .filter((item: any) => ["processing", "queued"].includes(item.jobItem?.status))
        .map((item: any) => item.id),
    );
  }, [items, dispatch]);

  return { resetPolling: startPolling, processingTasks, items };
};
